import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { IToolData } from 'types/products';
import { BordOneToneIcon, NudosButton, NudosTextInput } from 'components/NudosComponents';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { metabaseUrl } from 'constants/constants';
import { bordColorTheme } from 'styles/styles.constants';
import { updateDataSerialAndSKU } from 'services/services';
import './ProductEditSkuAndSerial.scss';

const ProductEditSkuAndSerial = ({
  productData,
  onClose,
  refreshToolsListing,
  setRefreshProductData
}: IProductEditSkuAndSerialProps) => {
  const { serial, sku } = productData ?? {};
  const [selectedSerial, setSelectedSerial] = useState<string>(serial ?? '');
  const [selectedSku, setSelectedSku] = useState<string>(sku ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const disableButtonSave = isLoading || !isEdit;

  const checkForChanges = () => {
    if (selectedSerial !== serial || selectedSku !== sku) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };
  const handleSave = async () => {
    if (!productData.productId || !selectedSerial || !selectedSku) return;
    try {
      setIsLoading(true);
      const data = {
        newSerial: selectedSerial,
        newSku: selectedSku
      };
      await updateDataSerialAndSKU(+productData.productId, data);
      await refreshToolsListing();
      setRefreshProductData(true);
      displaySuccessNotification({ customJSXMessage: <>Datos actualizados correctamente</> });
    } catch (error) {
      const err = error as AxiosError;
      if (err?.response?.data?.message?.toLowerCase().includes('serial')) {
        displayErrorNotification({ customJSXMessage: <>El serial no esta disponible intenta con otro</> });
      } else if (err?.response?.data?.message === 'sku not found') {
        displayErrorNotification({ customJSXMessage: <>No encontramos el SKU en la base de datos</> });
      } else {
        displayErrorNotification();
      }
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    checkForChanges();
  }, [selectedSerial, selectedSku]);

  return (
    <div className="productEditSkuAndSerial">
      <div className="title">Agrega o modifica el serial y SKU de la herramienta</div>
      <NudosTextInput
        isFilled={!!selectedSerial}
        label="Serial"
        placeholder="Serial"
        defaultValue={selectedSerial}
        handleChange={setSelectedSerial}
        customClassName="customSerialInput"
      />
      <NudosTextInput
        isFilled={!!selectedSku}
        label="SKU"
        placeholder="SKU"
        defaultValue={selectedSku}
        handleChange={setSelectedSku}
        customClassName="customSkuInput"
      />
      <div className="alertTextProductEditSkuAndSerial">
        <div className="alerIcon">
          <BordOneToneIcon
            variant="info"
            stroke={bordColorTheme.blue[400]}
            fill={bordColorTheme.white.default}
            strokeWidth={2}
          />
        </div>
        <div className="alertTextContent">
          <div className="titleAlert">Encuentra SKUs más rápido</div>
          <div className="subtitleAlert">
            Usa Metabase para buscar SKUs por modelo, pills y otros filtros de forma más rápida
          </div>
          <a className="linkContainer" href={metabaseUrl} target="_blank" rel="noopener noreferrer">
            Buscar SKUs
          </a>
        </div>
      </div>
      <div className="containerButtons">
        <NudosButton
          buttonText="Cancelar modificación"
          componentSize="small"
          handleClick={handleCancel}
          colorPalette="secondary"
          isButtonLoading={isLoading}
          isButtonDisabled={isLoading}
        />
        <NudosButton
          buttonText="Modificar datos de la herramienta"
          handleClick={handleSave}
          isButtonLoading={isLoading}
          isButtonDisabled={disableButtonSave}
        />
      </div>
    </div>
  );
};

export default ProductEditSkuAndSerial;

interface IProductEditSkuAndSerialProps {
  productData: IToolData;
  onClose: () => void;
  refreshToolsListing: () => void;
  setRefreshProductData: React.Dispatch<React.SetStateAction<boolean>>;
}
